import { SESSION_COOKIE_NAME } from '@/constants/settings';
import { env } from '@/environment';
import {
  type FEATURE_FLAGS_ALL_VALUES,
  FEATURE_FLAGS_DEFAULTS,
  type FEATURE_FLAGS_VALUE,
} from '@/helpers/feature-flag/model/constants';
import { isServerSide } from '@/helpers/isServerSide';
import { log } from '@/helpers/util/logger';
import getEnvTimeSettings from '@/store/fetchableParts/helpers/getEnvTimeSettings';

import type { FeatureFlagProvider, FeatureFlagResult } from '../base/Provider';

export class CustomFeatureFlagProvider implements FeatureFlagProvider {
  tag = 'Custom';

  async initialize(): Promise<void> {
    if (isServerSide()) {
      await this.getAllFlagsSSR();
    }
  }

  async getBooleanFlag(flagKey: FEATURE_FLAGS_VALUE, defaultValue: boolean): Promise<FeatureFlagResult> {
    try {
      const allFeatureFlags = await this.getAllFlagsSSR();

      const value = allFeatureFlags[flagKey];

      return {
        reason: 'PROVIDER',
        value: value ?? defaultValue,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `${this.tag}: error on getting flag ${flagKey}`;

      log(errorMessage);

      return {
        error: errorMessage,
        reason: 'ERROR',
        value: defaultValue,
      };
    }
  }

  private readonly TIMEOUT_FLAG_FETCH = 2000;

  async getAllFlagsSSR(): Promise<FEATURE_FLAGS_ALL_VALUES> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_FLAG_FETCH);

    try {
      const url = `${env.FFA_BASE_URL}/featureflags`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(isServerSide() ? { Cookie: `${SESSION_COOKIE_NAME}=${SESSION_COOKIE_NAME}` } : {}),
        },
        method: 'GET',
        next: { revalidate: getEnvTimeSettings().revalidate },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorMessage = `${this.tag}: failed to fetch flags from ${url}, status: ${response.status}`;

        log(errorMessage);

        throw new Error(errorMessage);
      }

      const data = (await response.json()) as FEATURE_FLAGS_ALL_VALUES;

      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `${this.tag}: unexpected error on fetching flags`;

      log(errorMessage);

      clearTimeout(timeoutId);

      return FEATURE_FLAGS_DEFAULTS;
    }
  }

  async dispose(): Promise<void> {}
}
